/**
 * Le détail d'un profil d'architecte : son identité, ses affaires, et le
 * GLOSSAIRE CONSOLIDÉ de tout le bureau — symboles, menuiseries, légendes et
 * nomenclature fusionnés sur l'ensemble de ses affaires. C'est là que le
 * réemploi des conventions d'un architecte devient une référence unique.
 *
 * La vue consolidée réutilise telles quelles les sections du glossaire d'affaire
 * (`ResumeVue`, `GlossaireCategories`) : même forme de rapport, même rendu.
 */
import { useState } from "react";

import { api, ErreurApi } from "../api/client";
import type { EntreeCatalogue, ProfilDetail } from "../api/types";
import { Avis } from "../composants/avis";
import { Chargement, Echec } from "../composants/etats";
import { useDonnees } from "../composants/donnees";
import { Lien, naviguer } from "../routeur";
import { GlossaireCategories, ResumeVue } from "./affaire/Glossaire";

export function Profil({ profilId }: { profilId: string }) {
  const detail = useDonnees<ProfilDetail>(
    () => api.lire<ProfilDetail>(`/api/v1/profils/${profilId}`),
    [profilId],
  );
  const [edition, poserEdition] = useState(false);
  const [erreur, poserErreur] = useState("");

  if (detail.charge && !detail.donnees) {
    return <div className="contenu"><Chargement lignes={4} /></div>;
  }
  if (detail.erreur) {
    return (
      <div className="contenu">
        <Echec erreur={detail.erreur} reessayer={detail.recharger} />
      </div>
    );
  }
  const vue = detail.donnees;
  if (!vue) return null;
  const { profil, affaires, glossaire } = vue;

  async function supprimer() {
    if (!window.confirm(`Supprimer le profil « ${profil.nom} » ? `
        + "Ses affaires seront détachées, pas supprimées.")) return;
    try {
      await api.retirer(`/api/v1/profils/${profilId}`);
      naviguer("/profils");
    } catch (exc) {
      poserErreur(exc instanceof ErreurApi ? exc.message
        : "Le profil n'a pas pu être supprimé.");
    }
  }

  return (
    <div className="ecran">
      <div className="ecran__tete">
        <div className="ariane discret">
          <Lien vers="/profils">Profils</Lien> / {profil.nom}
        </div>
        <div className="rangee rangee--espacee rangee--bas">
          <div className="pile pile--serree" style={{ minWidth: 0 }}>
            <h1 className="ecran__titre">{profil.nom}</h1>
            <div className="ecran__compte">
              {[profil.cabinet, profil.contact, profil.courriel, profil.telephone]
                .filter(Boolean).join(" · ") || "Bureau d'architecte"}
            </div>
          </div>
          <div className="rangee" style={{ gap: "var(--mo-space-2)" }}>
            <button type="button" className="bouton bouton--discret"
                    onClick={() => poserEdition((e) => !e)}>Modifier</button>
            <button type="button" className="bouton bouton--discret"
                    onClick={supprimer}>Supprimer</button>
          </div>
        </div>
      </div>

      <div className="ecran__corps pile">
        {erreur && <Avis ton="erreur" cle={erreur}>{erreur}</Avis>}
        {edition && (
          <EditionProfil detail={vue}
                         surFait={() => { poserEdition(false); detail.recharger(); }}
                         surAnnule={() => poserEdition(false)} />
        )}
        {profil.notes && (
          <div className="carte"><div className="carte__corps">{profil.notes}</div></div>
        )}

        <div className="carte">
          <div className="carte__corps pile pile--serree">
            <div className="rangee rangee--espacee">
              <h3 style={{ margin: 0 }}>Affaires du profil</h3>
              <span className="discret">
                {affaires.length} affaire{affaires.length > 1 ? "s" : ""} ·{" "}
                {vue.avec_glossaire} avec glossaire
              </span>
            </div>
            {affaires.length === 0 ? (
              <span className="discret">
                Aucune affaire affectée. Depuis l'onglet <b>Glossaire</b> d'une
                affaire, choisissez « Affecter à un profil ».
              </span>
            ) : (
              <div className="pile pile--serree">
                {affaires.map((a) => (
                  <div key={a.affaire_id} className="rangee rangee--espacee profil-affaire">
                    <Lien vers={`/affaires/${a.affaire_id}?onglet=glossaire`}>
                      <span className="mono">{a.reference}</span>
                      {a.label ? <span className="discret"> — {a.label}</span> : null}
                    </Lien>
                    <span className="discret">
                      {a.glossaire ? "glossaire prêt" : "sans glossaire"}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <CatalogueBureau profilId={profilId} initial={vue.catalogue}
                         affaires={affaires}
                         surEnregistre={() => detail.recharger()} />

        <h2 style={{ margin: "var(--mo-space-2) 0 0" }}>Glossaire consolidé</h2>
        {glossaire ? (
          <>
            <div className="carte"><div className="carte__corps">
              <ResumeVue rapport={glossaire} />
            </div></div>
            <GlossaireCategories rapport={glossaire} />
          </>
        ) : (
          <Avis ton="info" permanent>
            Le glossaire consolidé apparaîtra dès qu'une affaire affectée à ce
            profil aura un inventaire prêt. Lancez l'inventaire depuis l'onglet
            Glossaire d'une affaire, puis affectez-la à ce profil.
          </Avis>
        )}
      </div>
    </div>
  );
}

/** Édition en place des champs d'un profil. */
function EditionProfil({
  detail, surFait, surAnnule,
}: {
  detail: ProfilDetail;
  surFait: () => void;
  surAnnule: () => void;
}) {
  const p = detail.profil;
  const [champs, poser] = useState({
    nom: p.nom, cabinet: p.cabinet, contact: p.contact,
    courriel: p.courriel, telephone: p.telephone, notes: p.notes,
  });
  const [envoi, poserEnvoi] = useState(false);
  const [erreur, poserErreur] = useState("");
  const set = (k: keyof typeof champs) => (v: string) =>
    poser((c) => ({ ...c, [k]: v }));

  async function enregistrer() {
    poserEnvoi(true);
    poserErreur("");
    try {
      await api.modifier(`/api/v1/profils/${p.profil_id}`, champs);
      surFait();
    } catch (exc) {
      poserErreur(exc instanceof ErreurApi ? exc.message
        : "Les modifications n'ont pas pu être enregistrées.");
    } finally {
      poserEnvoi(false);
    }
  }

  const chp = (k: keyof typeof champs, label: string, max: number) => (
    <div className="champ">
      <label htmlFor={`e-${k}`}>{label}</label>
      <input id={`e-${k}`} value={champs[k]} maxLength={max}
             onChange={(e) => set(k)(e.target.value)} />
    </div>
  );

  return (
    <div className="carte">
      <div className="carte__corps pile">
        <h3 style={{ margin: 0 }}>Modifier le profil</h3>
        {erreur && <Avis ton="erreur" cle={erreur}>{erreur}</Avis>}
        <div className="grille grille--2">
          {chp("nom", "Nom *", 120)}
          {chp("cabinet", "Cabinet / agence", 200)}
          {chp("contact", "Contact", 120)}
          {chp("courriel", "Courriel", 200)}
          {chp("telephone", "Téléphone", 60)}
        </div>
        <div className="champ">
          <label htmlFor="e-notes">Notes</label>
          <textarea id="e-notes" value={champs.notes} maxLength={2000} rows={2}
                    onChange={(e) => set("notes")(e.target.value)} />
        </div>
        <div className="rangee" style={{ gap: "var(--mo-space-2)" }}>
          <button type="button" className="bouton bouton--primaire"
                  disabled={envoi || !champs.nom.trim()} onClick={enregistrer}>
            {envoi ? "Enregistrement…" : "Enregistrer"}
          </button>
          <button type="button" className="bouton bouton--discret"
                  onClick={surAnnule}>Annuler</button>
        </div>
      </div>
    </div>
  );
}

/** Le catalogue d'ouvrages du bureau — les colonnes de métré que ce cabinet
 *  réutilise d'une affaire à l'autre. Éditable, et alimentable en un geste
 *  depuis les ouvrages VALIDÉS de l'inventaire d'une affaire du profil.
 *
 *  **Des libellés, jamais des nombres.** Une entrée dit QUELLE colonne chiffrer
 *  (libellé, unité, groupe) ; la quantité, elle, se relève par affaire. */
function CatalogueBureau({
  profilId, initial, affaires, surEnregistre,
}: {
  profilId: string;
  initial: EntreeCatalogue[];
  affaires: { affaire_id: string; reference: string; label: string | null }[];
  surEnregistre: () => void;
}) {
  const [entrees, poser] = useState<EntreeCatalogue[]>(initial);
  const [dirty, poserDirty] = useState(false);
  const [erreur, poserErreur] = useState("");
  const [info, poserInfo] = useState("");
  const [enreg, poserEnreg] = useState(false);
  const [importDe, poserImportDe] = useState("");
  const [importe, poserImporte] = useState(false);

  const maj = (n: EntreeCatalogue[]) => { poser(n); poserDirty(true); };
  const modifier = (i: number, champ: keyof EntreeCatalogue, v: string) =>
    maj(entrees.map((e, j) => (j === i ? { ...e, [champ]: v } : e)));
  const retirer = (i: number) => maj(entrees.filter((_, j) => j !== i));
  const ajouter = () =>
    maj([...entrees, { clef: "", libelle: "", unite: "", groupe: "Divers" }]);

  async function reprendre() {
    if (!importDe) return;
    poserImporte(true); poserErreur(""); poserInfo("");
    try {
      const r = await api.lire<{ donnees: { cle: string; libelle: string;
        unite: string; groupe: string; statut: string }[] | null }>(
        `/api/v1/affaires/${importDe}/inventaire`);
      const valides = (r.donnees ?? []).filter((o) => o.statut === "valide");
      const vus = new Set(entrees.map((e) => e.libelle.trim().toLowerCase()));
      const neufs = valides
        .filter((o) => !vus.has((o.libelle || "").trim().toLowerCase()))
        .map((o) => ({ clef: o.cle, libelle: o.libelle, unite: o.unite,
                       groupe: o.groupe || "Divers" }));
      if (neufs.length === 0) {
        poserInfo(valides.length
          ? "Ces ouvrages validés sont déjà dans le catalogue."
          : "Cette affaire n'a aucun ouvrage validé à reprendre.");
      } else {
        maj([...entrees, ...neufs]);
        poserInfo(`${neufs.length} ouvrage(s) validé(s) repris.`);
      }
    } catch (exc) {
      poserErreur(exc instanceof ErreurApi ? exc.message
        : "L'inventaire de cette affaire n'a pas pu être lu.");
    } finally {
      poserImporte(false);
    }
  }

  async function enregistrer() {
    poserEnreg(true); poserErreur(""); poserInfo("");
    try {
      const propres = entrees.filter((e) => e.libelle.trim());
      const r = await api.remplacer<{ donnees: { entrees: number } }>(
        `/api/v1/profils/${profilId}/catalogue`, { entrees: propres });
      poserDirty(false);
      poserInfo(r.donnees.entrees
        ? `Catalogue enregistré — ${r.donnees.entrees} ouvrage(s).`
        : "Catalogue vidé — les affaires retomberont sur le catalogue proposé.");
      surEnregistre();
    } catch (exc) {
      poserErreur(exc instanceof ErreurApi ? exc.message
        : "Le catalogue n'a pas pu être enregistré.");
    } finally {
      poserEnreg(false);
    }
  }

  return (
    <div className="carte">
      <div className="carte__corps pile pile--serree">
        <div className="rangee rangee--espacee">
          <div className="pile pile--serree">
            <h3 style={{ margin: 0 }}>Catalogue d'ouvrages du bureau</h3>
            <span className="discret">
              Les colonnes de métré réutilisées par ce cabinet. Elles priment sur
              le catalogue proposé quand une affaire est affectée à ce profil.
            </span>
          </div>
          <button type="button" className="bouton bouton--primaire"
                  disabled={!dirty || enreg} onClick={() => void enregistrer()}>
            {enreg ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>

        {erreur && <Avis ton="erreur" cle={erreur}>{erreur}</Avis>}
        {info && <span className="discret" role="status">{info}</span>}

        {entrees.length > 0 && (
          <div className="defilant">
            <table className="tableau cat-table">
              <thead><tr>
                <th>Ouvrage</th><th>Unité</th><th>Groupe</th><th aria-label="Retirer" />
              </tr></thead>
              <tbody>
                {entrees.map((e, i) => (
                  <tr key={i}>
                    <td><input value={e.libelle} placeholder="Libellé de l'ouvrage"
                               onChange={(ev) => modifier(i, "libelle", ev.target.value)} /></td>
                    <td><input value={e.unite} placeholder="m², ml…" className="cat-unite"
                               onChange={(ev) => modifier(i, "unite", ev.target.value)} /></td>
                    <td><input value={e.groupe} placeholder="Groupe"
                               onChange={(ev) => modifier(i, "groupe", ev.target.value)} /></td>
                    <td><button type="button" className="cat-retirer"
                                aria-label="Retirer" onClick={() => retirer(i)}>✕</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="rangee" style={{ gap: "var(--mo-space-2)", flexWrap: "wrap" }}>
          <button type="button" className="bouton bouton--discret" onClick={ajouter}>
            + Ajouter un ouvrage
          </button>
          {affaires.length > 0 && (
            <div className="rangee" style={{ gap: "var(--mo-space-2)" }}>
              <select value={importDe} onChange={(e) => poserImportDe(e.target.value)}
                      aria-label="Affaire d'où reprendre les ouvrages validés">
                <option value="">Reprendre d'une affaire…</option>
                {affaires.map((a) => (
                  <option key={a.affaire_id} value={a.affaire_id}>
                    {a.reference}{a.label ? ` — ${a.label}` : ""}
                  </option>
                ))}
              </select>
              <button type="button" className="bouton bouton--discret"
                      disabled={!importDe || importe} onClick={() => void reprendre()}>
                {importe ? "…" : "Reprendre les validés"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
